/**
 * Canonical Severity Model → Risk Severity Engine
 *
 * Scoring contract (2026.2 / weighted-gravity-10-6-4-2-v2):
 * - YES = satisfactory / no risk contribution.
 * - NO = finding / full gravity contribution.
 * - UNSURE = investigation finding / full gravity contribution (conservative).
 * - Numeric gravity is honored on the AFP 10 / 6 / 4 / 2 scale.
 * - risk_score is weighted finding pressure across the full active bank.
 * - risk_grade adds a severity floor so a critical/high-risk finding cannot be
 *   hidden by a low aggregate percentage.
 * - severity_tier is numeric 1..5 for exposure modeling.
 *
 * Backward-compatible output names are retained where practical.
 */

const LEGACY_STRING_GRAVITY_WEIGHTS = Object.freeze({
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
});

const GRADE_ORDER = Object.freeze(["A", "B", "C", "D", "E"]);
const RISK_GRADE_LABELS = Object.freeze({
  A: "Low Risk",
  B: "Guarded Risk",
  C: "Moderate Risk",
  D: "High Risk",
  E: "Severe Risk",
});

function normalizeGravity(gravity) {
  if (gravity === undefined || gravity === null || gravity === "") return 1;

  if (typeof gravity === "number") {
    return Math.max(1, Math.min(10, Math.round(gravity)));
  }

  const raw = String(gravity).toLowerCase().trim();
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return Math.max(1, Math.min(10, Math.round(numeric)));
  return LEGACY_STRING_GRAVITY_WEIGHTS[raw] || 1;
}

function normalizeAnswerValue(value) {
  if (value === true) return "yes";
  if (value === false) return "no";
  const normalized = String(value ?? "").toLowerCase().trim();
  if (normalized === "yes" || normalized === "true") return "yes";
  if (normalized === "no" || normalized === "false") return "no";
  if (normalized === "unsure") return "unsure";
  return "";
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeQuestionRecord(question, index = 0) {
  if (!isPlainObject(question)) return null;

  const idCandidates = [
    question.id,
    question.question_id,
    question.questionId,
    question.key,
    question.slug,
    question.code,
    question.uuid,
    question.number,
    question.question_number,
    question.name,
  ];

  const id = idCandidates.find(
    (candidate) => candidate !== undefined && candidate !== null && String(candidate).trim() !== "",
  );

  if (id === undefined || id === null || String(id).trim() === "") {
    return { ...question, id: `question_${index + 1}` };
  }

  return { ...question, id: String(id) };
}

function flattenQuestionPayload(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input.flatMap((item) => flattenQuestionPayload(item));
  if (!isPlainObject(input)) return [];

  const directQuestion = normalizeQuestionRecord(input);
  const hasQuestionSignals = [
    input.id,
    input.question_id,
    input.questionId,
    input.gravity,
    input.text,
    input.question_text,
    input.prompt,
    input.category,
  ].some((value) => value !== undefined && value !== null && String(value).trim() !== "");

  if (hasQuestionSignals && directQuestion) return [directQuestion];

  const containerKeys = ["questions", "items", "rows", "data", "sections", "groups", "pages", "steps", "controls"];
  const collected = [];
  containerKeys.forEach((key) => {
    if (input[key] !== undefined) collected.push(...flattenQuestionPayload(input[key]));
  });
  if (collected.length) return collected;

  return Object.values(input).flatMap((value) => flattenQuestionPayload(value));
}

function normalizeQuestions(questionsInput) {
  const normalized = flattenQuestionPayload(questionsInput)
    .map((question, index) => normalizeQuestionRecord(question, index))
    .filter(Boolean);

  const seen = new Set();
  return normalized.filter((question) => {
    const key = String(question.id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeAnswers(answersInput = {}) {
  if (Array.isArray(answersInput)) {
    return answersInput.reduce((acc, row, index) => {
      if (!isPlainObject(row)) return acc;
      const key = row.id ?? row.question_id ?? row.questionId ?? row.key ?? index;
      const value = row.answer ?? row.value ?? row.response ?? row.selected ?? "";
      acc[String(key)] = value;
      return acc;
    }, {});
  }

  if (isPlainObject(answersInput)) {
    if (isPlainObject(answersInput.answers)) return normalizeAnswers(answersInput.answers);
    if (Array.isArray(answersInput.answers)) return normalizeAnswers(answersInput.answers);
    return { ...answersInput };
  }

  return {};
}

export function gradeFromRiskScore(riskScore) {
  const score = Number(riskScore);
  if (!Number.isFinite(score)) return "A";
  if (score <= 15) return "A";
  if (score <= 30) return "B";
  if (score <= 50) return "C";
  if (score <= 75) return "D";
  return "E";
}

function moreSevereGrade(a, b) {
  const ai = GRADE_ORDER.indexOf(String(a || "A").toUpperCase());
  const bi = GRADE_ORDER.indexOf(String(b || "A").toUpperCase());
  return GRADE_ORDER[Math.max(ai < 0 ? 0 : ai, bi < 0 ? 0 : bi)];
}

export function numericSeverityTierFromGrade(grade) {
  const idx = GRADE_ORDER.indexOf(String(grade || "A").toUpperCase());
  return idx < 0 ? 1 : idx + 1;
}

export function numericSeverityTierFromRiskScore(riskScore) {
  return numericSeverityTierFromGrade(gradeFromRiskScore(riskScore));
}

function gradeFloorFromMaxFindingGravity(maxGravity) {
  const gravity = Number(maxGravity) || 0;
  if (gravity >= 10) return "D";
  if (gravity >= 6) return "C";
  return "A";
}

export function computeSeverityFromAnswers(questions = [], answers = {}) {
  const safeQuestions = normalizeQuestions(questions);
  const safeAnswers = normalizeAnswers(answers);

  let totalPossible = 0;
  let totalFailed = 0;
  let maxFindingGravity = 0;

  const failed_question_ids = [];
  const unsure_question_ids = [];
  const critical_finding_ids = [];
  const high_risk_finding_ids = [];

  safeQuestions.forEach((q) => {
    const weight = normalizeGravity(q.gravity);
    totalPossible += weight;

    const answer = normalizeAnswerValue(safeAnswers[q.id]);
    if (answer !== "no" && answer !== "unsure") return;

    totalFailed += weight;
    maxFindingGravity = Math.max(maxFindingGravity, weight);

    if (answer === "no") failed_question_ids.push(q.id);
    else unsure_question_ids.push(q.id);

    if (weight >= 10) critical_finding_ids.push(q.id);
    if (weight >= 6) high_risk_finding_ids.push(q.id);
  });

  const risk_score = totalPossible === 0 ? 0 : Math.round((totalFailed / totalPossible) * 100);
  const risk_grade_base = gradeFromRiskScore(risk_score);
  const risk_grade_floor = gradeFloorFromMaxFindingGravity(maxFindingGravity);
  const risk_grade = moreSevereGrade(risk_grade_base, risk_grade_floor);
  const severity_tier = numericSeverityTierFromGrade(risk_grade);

  return {
    risk_score,
    weighted_risk_score: risk_score,
    risk_grade,
    risk_grade_base,
    risk_grade_floor,
    risk_grade_floor_applied: risk_grade !== risk_grade_base,
    risk_grade_label: RISK_GRADE_LABELS[risk_grade],

    // Backward compatibility and explicit numeric exposure input.
    severity_score_0_100: risk_score,
    severity_tier,
    severity_tier_numeric: severity_tier,

    max_finding_gravity: maxFindingGravity,
    critical_finding_ids,
    high_risk_finding_ids,
    failed_question_ids,
    unsure_question_ids,

    explain: {
      totalPossible,
      totalFailed,
      normalized_question_count: safeQuestions.length,
      gravity_contract: "10_6_4_2",
      answer_contract: "YES_PASS_V1",
      grade_floor_policy: "GRAVITY10_MIN_D__GRAVITY6_MIN_C",
    },
  };
}

export { normalizeGravity, normalizeAnswerValue };
export default computeSeverityFromAnswers;
